import { Suspense, lazy } from 'react';
import type { VisualComponent } from '@dtwin/shared';
import { makeWidgetClickHandler } from '../../core/widgetEventDispatch';

const ChartRenderer = lazy(() => import('../charts/ChartRenderer'));

/** 读取组件属性（base > config > style > default），与编辑器 readBase 同链 */
export function readCompProp(comp: VisualComponent, key: string, fallback: unknown): any {
  const c = comp as any;
  if (c?.base && c.base[key] !== undefined) return c.base[key];
  if (c?.config && c.config[key] !== undefined) return c.config[key];
  if (c?.style && c.style[key] !== undefined) return c.style[key];
  return fallback;
}

interface CompPreviewBoxProps {
  comp: VisualComponent;
  /** 是否允许点击交互（默认不允许：预览覆盖层 pointer-events:none） */
  interactive?: boolean;
}

/**
 * 2D 大屏组件「渲染盒」：与编辑器 DraggableComponent 渲染属性完全一致
 * （visible/zIndex/边框/圆角/阴影/旋转/背景色/背景图/透明度/标题），
 * 供预览/发布/联动预览等宿主统一使用，避免各宿主属性应用不一致。
 */
export default function CompPreviewBox({ comp, interactive = false }: CompPreviewBoxProps) {
  const visible = readCompProp(comp, 'visible', true) as boolean;
  const zIndex = readCompProp(comp, 'zIndex', 1) as number;
  const borderRadius = readCompProp(comp, 'borderRadius', 4) as number;
  const borderWidth = readCompProp(comp, 'borderWidth', 0) as number;
  const borderColor = readCompProp(comp, 'borderColor', '#334155') as string;
  const boxShadow = readCompProp(comp, 'boxShadow', 'none') as string;
  const bgColor = readCompProp(comp, 'bgColor', 'transparent') as string;
  const bgImage = readCompProp(comp, 'bgImage', '') as string;
  const bgOpacity = readCompProp(comp, 'bgOpacity', 1) as number;
  const rotation = Number(readCompProp(comp, 'rotation', 0)) || 0;
  const title = (readCompProp(comp, 'title', '') as string) || '';
  const titleFontSize = readCompProp(comp, 'titleFontSize', 14) as number;
  const titleFontFamily = readCompProp(comp, 'titleFontFamily', '') as string;
  const fontFamily = readCompProp(comp, 'fontFamily', '') as string;
  const titleColor = readCompProp(comp, 'titleColor', '#ffffff') as string;
  const titleAlign = readCompProp(comp, 'titleAlign', 'center') as string;
  const titlePosition = readCompProp(comp, 'titlePosition', 'top') as string;

  return (
    <div
      data-testid="dash-preview-comp"
      data-comp-id={comp.id}
      onClick={interactive ? () => makeWidgetClickHandler(comp)() : undefined}
      style={{
        display: visible ? 'block' : 'none',
        position: 'absolute',
        left: comp.position.x,
        top: comp.position.y,
        width: comp.size.width,
        height: comp.size.height,
        zIndex,
        boxSizing: 'border-box',
        overflow: 'hidden',
        borderRadius,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        // 2026-08-26｜联动预览点击穿透修复：预览覆盖层容器 pointer-events:none，
        // 子组件不显式 auto 时整棵树不可交互（设备表格行点击被 3D canvas 拦截，无法驱动 3D 联动飞行）
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      {/* 背景层（透明度只作用于背景） */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          borderRadius: Math.max(0, borderRadius - 2),
          background: bgColor,
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundSize: bgImage ? 'cover' : undefined,
          opacity: bgOpacity,
        }}
      />
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', borderRadius, fontFamily: fontFamily || undefined }}>
        <Suspense fallback={null}>
          <ChartRenderer component={comp} />
        </Suspense>
      </div>
      {title && (
        <div
          style={{
            position: 'absolute',
            [titlePosition === 'bottom' ? 'bottom' : 'top']: 0,
            left: 0,
            right: 0,
            zIndex: 5,
            padding: '4px 10px',
            fontSize: titleFontSize,
            fontFamily: titleFontFamily || undefined,
            color: titleColor,
            textAlign: titleAlign === 'left' ? 'left' : titleAlign === 'right' ? 'right' : 'center',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 100%)',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
}